import { useState } from "react";
import { useDispatch } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import { actionDataSearchRooms } from "./dataRedux";
import {
  actionMenuSearchTypeToggled,
  actionMenuSearchUpdateChanged,
} from "../AppBar";

const add_ = (theme) => ({
  color: "rgb(224 224 224)",
  marginLeft: "-4px",
  "&:hover": {
    color: "rgba(0, 0, 0, 0.55)",
  },
});

const chip_ = (theme) => ({
  marginRight: "4px",
  marginBottom: "2px",
  "& .MuiAvatar-root": {
    width: 24,
    height: 24,
    fontSize: "0.7rem",
    marginLeft: "0px",
    marginRight: "-4px",
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  "& .MuiChip-label": {
    paddingLeft: "8px",
    paddingRight: "8px",
  },
});

const chipLabel_ = (theme) => ({
  fontSize: "0.65rem",
  letterSpacing: "0",
});

const FILTER_AVATAR_TEXT = {
  building: "BLD",
  room: "RM",
  person: "P",
  department: "DEP",
  occdepartment: "ODEP",
  roomtype: "TYP",
  roomsubtype: "STYP",
};

export default function Filters({ filters, id }) {
  const dispatch = useDispatch();

  const handleDelete = (filterId) => {
    const newFilters = filters.filter((filter) => filter.id !== filterId);
    dispatch(actionDataSearchRooms(newFilters, id));
  };

  const handleChipClick = (filter) => {
    const searchType = filter.type === "roomsubtype" ? "roomtype" : filter.type;
    dispatch(
      actionMenuSearchUpdateChanged({
        groupId: id,
        type: "replace",
        targetFilterId: filter.id,
      }),
    );
    dispatch(actionMenuSearchTypeToggled(searchType));
  };

  return (
    <>
      {filters.map((filter) => {
        return (
          <Filter
            key={filter.id}
            filter={filter}
            handleDelete={handleDelete}
            handleChipClick={handleChipClick}
          />
        );
      })}
      <AddFilter id={id} />
    </>
  );
}

function Filter({ filter, handleDelete, handleChipClick }) {
  const onDelete = () => {
    handleDelete(filter.id);
  };
  const onClick = () => {
    handleChipClick(filter);
  };

  return (
    <Chip
      size="small"
      sx={chip_}
      onClick={onClick}
      onDelete={onDelete}
      avatar={
        <Tooltip title={toTooltip(filter)}>
          <Avatar data-testid="search-result-filter-type">
            {avatarText(filter)}
          </Avatar>
        </Tooltip>
      }
      label={
        <Typography
          variant="overline"
          sx={chipLabel_}
          data-testid="search-result-filter-name"
        >
          {toText(filter)}
        </Typography>
      }
    />
  );
}

function AddFilter({ id }) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (searchType) => () => {
    dispatch(actionMenuSearchUpdateChanged({ groupId: id, type: "add" }));
    dispatch(actionMenuSearchTypeToggled(searchType));
    handleClose();
  };

  return (
    <>
      <Tooltip title="Add Search Criteria">
        <IconButton size="small" sx={add_} onClick={handleClick}>
          <AddCircleIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="add-search-criteria"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSelect("building")}>
          <Typography variant="overline">Building</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelect("room")}>
          <Typography variant="overline">Room</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelect("person")}>
          <Typography variant="overline">People</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelect("department")}>
          <Typography variant="overline">Department</Typography>
        </MenuItem>
        <MenuItem onClick={handleSelect("roomtype")}>
          <Typography variant="overline">Room Type</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

export function toText(filter) {
  if (filter.label) {
    return filter.label;
  } else if (filter.type === "building") {
    return filter.bld_descrshort || filter.bldrecnbr;
  } else if (filter.type === "room") {
    return filter.rmnbr;
  } else if (filter.type === "person") {
    return filter.person_uniqname.toLowerCase();
  } else if (filter.type === "department") {
    return filter.dept_descr;
  } else if (filter.type === "roomtype") {
    return filter.rmtyp;
  } else if (filter.type === "roomsubtype") {
    return filter.rmtyp + "-" + filter.rmsubtyp;
  } else {
    return "";
  }
}

function avatarText(filter) {
  return FILTER_AVATAR_TEXT[filter.type] ? FILTER_AVATAR_TEXT[filter.type] : "";
}

function toTooltip(filter) {
  if (filter.type === "occdepartment") {
    return "OCCUPYING DEPARTMENT";
  }
  return filter.type && filter.type.toUpperCase
    ? filter.type.toUpperCase()
    : "";
}
